import {ResourceType} from '../../../@types/resource-type';

/**
 * Configuration for type-specific Explorer behavior
 */
export interface ExplorerTypeConfig {
    defaultOrderBy: string;
    showGroupSelector: boolean;
    nameColumnLabel: string;
    showTable?: boolean;
    showDatePicker?: boolean;
    showNewButton?: boolean;
    showDescriptorTypeSelector?: boolean;
    useOccurrenceReportTable?: boolean;
    useNewOccurrenceReportTable?: boolean;
    useOccurrenceReportDescriptorTable?: boolean;
}

/**
 * Type-specific configurations for the Explorer component
 */
export const EXPLORER_TYPE_CONFIG: Record<ResourceType | 'default', ExplorerTypeConfig> = {
    [ResourceType.OCCURRENCE_REPORTS]: {
        defaultOrderBy: 'uniqueId',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: false,
        useOccurrenceReportTable: true,
    },
    [ResourceType.NEW_OCCURRENCE_REPORTS]: {
        defaultOrderBy: 'uniqueId',
        showGroupSelector: true,
        showDatePicker: true,
        showNewButton: false,
        nameColumnLabel: 'Name',
        showTable: false,
        useNewOccurrenceReportTable: true,
    },
    [ResourceType.FOUNDRY_EVENTS]: {
        defaultOrderBy: 'uniqueId',
        showGroupSelector: false,
        showDatePicker: true,
        showNewButton: false,
        nameColumnLabel: 'Name',
        showTable: false,
        useNewOccurrenceReportTable: true,
    },
    [ResourceType.USERS]: {
        defaultOrderBy: 'name',
        showGroupSelector: false,
        nameColumnLabel: 'User Name',
        showTable: true,
    },
    [ResourceType.SERVICE_USER_ACCOUNTS]: {
        defaultOrderBy: 'name',
        showGroupSelector: false,
        nameColumnLabel: 'User Name',
        showTable: true,
    },
    [ResourceType.GROUPS]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.OCCURRENCE_REPORT_DESCRIPTORS]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        showDescriptorTypeSelector: true,
        nameColumnLabel: 'Name',
        showTable: false,
        useOccurrenceReportDescriptorTable: true,
    },
    [ResourceType.GROUP_ROLE_PERMISSIONS]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.SAFETY_MODELS]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.EVENT_SEQUENCE_HISTORY]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.EVENT_SEQUENCES]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.EVENT_SEQUENCE_TREES]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.EVENT_SEQUENCE_HISTORY_TREES]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.EVENT_SEQUENCE_SENSITIVIY_ANALYSIS]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.OCCURRENCE_TYPES]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.PERFORMANCE_SHAPING_FACTORS]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.TRAFFIC_MODELS]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.OPERATIONAL_CHANGES]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.RISK_FORECASTS]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.WORKSHOPS]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.INTEGRATED_RISK_PICTURES]: {
        defaultOrderBy: 'created',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
        showNewButton: false,
    },
    [ResourceType.IRP_WORKBOOK]: {
        defaultOrderBy: 'created',
        showGroupSelector: false,
        nameColumnLabel: 'Name',
        showTable: false,
        showNewButton: false,
    },
    [ResourceType.COMPONENT]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.COMPONENT_TREE]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.COMPONENT_MAPPING]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.COMPONENT_HISTORY]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.COMPONENT_HISTORY_TREE]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    // Safety recommendations are an explorer-mrt dataset only; the legacy explorer never lists
    // them, but this map must cover every resource type, so they take the plain defaults.
    [ResourceType.SAFETY_RECOMMENDATIONS]: {
        defaultOrderBy: 'name',
        showGroupSelector: false,
        nameColumnLabel: 'Source',
        showTable: true,
    },
    [ResourceType.SAFETY_RECOMMENDATION]: {
        defaultOrderBy: 'number',
        showGroupSelector: false,
        nameColumnLabel: 'Recommendation',
        showTable: true,
    },
    // The component catalogue is an explorer-mrt dataset only; the legacy explorer never lists
    // it, but this map must cover every resource type, so it takes the plain defaults.
    [ResourceType.COMPONENT_CATALOGUE]: {
        defaultOrderBy: 'name',
        showGroupSelector: false,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    [ResourceType.UNKNOWN]: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
    },
    default: {
        defaultOrderBy: 'name',
        showGroupSelector: true,
        nameColumnLabel: 'Name',
        showTable: true,
        showNewButton: true,
    }
};

/**
 * Gets the configuration for a specific resource type
 */
export function getExplorerConfig(type: ResourceType): ExplorerTypeConfig {
    return EXPLORER_TYPE_CONFIG[type] || EXPLORER_TYPE_CONFIG.default;
}